import { useEffect, useState } from "react";
import axiosInstance, { handleApiError } from "../../../../services/axiosService";
import SingleSelectBox from "../../../FormComponents/SingleSelectBox";
import VariableMapping from "../VariableMapping/VariableMapping";
import CustomJS from "../../CustomJS";
import { extractVariables } from "../../../../commonFunctions/fileFolderConfig";
import Loader from "../../../../Layouts/Loader/Loader";

const HttpRequest = ({ tableType = "statemachine", tableId, httpRequestData, onHttpDataChange, formFields }) => {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const handleTemplateChange = (option) => {
    if (option?.id === selectedTemplate?.id) {
      return;
    }

    setSelectedTemplate(option);
    onHttpDataChange((prev) => ({
      ...prev,
      http_template_id: option?.id || null,
      url_variable_mapping: [],
      query_variable_mapping: [],
      header_variable_mapping: [],
      body_variable_mapping: [],
      output_variable_mapping: [],
      output_transformation_custom_js: ""
    }));
  };

  const handleChange = (key, updatedData) => {
    onHttpDataChange((prev) => {
      const updated = { ...prev };
      updated[key] = updatedData;
      return updated;
    });
  };

  const extractVariablesFromArray = (arrayData) => {
    if (!selectedTemplate || !Array.isArray(arrayData)) return [];

    const allVariableNames = new Set();

    arrayData.forEach((item) => {
      if (!item?.value) return;
      const newVars = extractVariables(item.value);
      newVars.forEach((name) => allVariableNames.add(name));
    });

    if (allVariableNames.size === 0) return [];

    return Array.from(allVariableNames);
  };

  const extractVariablesFromString = (str) => {
    if (!selectedTemplate || !str) return [];

    const variableNames = extractVariables(str);

    if (!variableNames.length) return [];

    return variableNames;
  };

  const extractVariablesFromOutputBindings = () => {
    if (!selectedTemplate || !Array.isArray(selectedTemplate.http_output_variable_bindings)) return [];

    const allVariableNames = new Set();

    selectedTemplate.http_output_variable_bindings.forEach((binding) => {
      if (!binding?.path) return [];
      const newVars = [binding.name];
      newVars.forEach((name) => allVariableNames.add(name));
    });

    if (allVariableNames.size === 0) return [];

    return Array.from(allVariableNames);
  };

  const extractVariablesFromQueryParams = () => {
    if (!selectedTemplate || !Array.isArray(selectedTemplate.query_params)) return [];

    return selectedTemplate.query_params;
  };

  const mergeParams = (newParams, existingParams) => {
    if (!newParams || newParams.length === 0) return [];

    if (!existingParams || existingParams.length === 0) {
      return newParams.map((name) => ({
        key: name,
        type: "mapped",
        value: ""
      }));
    }

    const existingMap = new Map(existingParams.map((param) => [param.key, param]));

    return newParams.map((key) => {
      const existing = existingMap.get(key);
      if (existing) {
        return existing;
      }
      return {
        key: key,
        type: "mapped",
        value: ""
      };
    });
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      setTemplatesError("");
      try {
        const resp = await axiosInstance.get("/http-request-templates");
        const respTemplates = resp?.data ?? [];
        if (!Array.isArray(respTemplates)) {
          setTemplates([]);
        } else {
          setTemplates(respTemplates);
        }

        const found = respTemplates.find((t) => t.id === httpRequestData.http_template_id);
        if (found) {
          setSelectedTemplate(found);
        }
      } catch (err) {
        handleApiError(err);
        setTemplatesError("Failed to load templates");
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedTemplate) return;

    var bodyParams, headerParams, urlParams, outputParams, queryParams;

    if (selectedTemplate?.body_raw_key_values?.length) {
      bodyParams = extractVariablesFromArray(selectedTemplate.body_raw_key_values);
    }
    if (selectedTemplate?.header_parameters?.length) {
      headerParams = extractVariablesFromArray(selectedTemplate.header_parameters);
    }
    if (selectedTemplate?.url) {
      urlParams = extractVariablesFromString(selectedTemplate.url);
    }
    if (selectedTemplate?.http_output_variable_bindings?.length) {
      outputParams = extractVariablesFromOutputBindings();
    }
    if (selectedTemplate?.query_params?.length) {
      queryParams = extractVariablesFromQueryParams();
    }

    onHttpDataChange((prev) => ({
      ...prev,
      url_variable_mapping: mergeParams(urlParams, prev.url_variable_mapping),
      query_variable_mapping: mergeParams(queryParams, prev.query_variable_mapping),
      header_variable_mapping: mergeParams(headerParams, prev.header_variable_mapping),
      body_variable_mapping: mergeParams(bodyParams, prev.body_variable_mapping),
      output_variable_mapping: mergeParams(outputParams, prev.output_variable_mapping)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  if (loadingTemplates) {
    return <Loader />;
  }

  if (templatesError) {
    return (
      <p style={{ color: "red" }} className="h_100 w_100 d_flex justify_center align_center">
        error:{templatesError}
      </p>
    );
  }

  return (
    <div className="flex flex_column gap_3">
      <div>
        <label style={{ display: "block", marginBottom: 6 }}>Select HTTP Template</label>
        <SingleSelectBox
          options={templates}
          valueKey="id"
          labelKey={["name"]}
          selectedData={selectedTemplate}
          onChange={handleTemplateChange}
          placeholder="Choose an HTTP template"
          isSearch={true}
        />
      </div>
      {selectedTemplate ? (
        <>
          <div>
            <VariableMapping
              variableData={httpRequestData?.url_variable_mapping}
              onChange={(updatedData) => handleChange("url_variable_mapping", updatedData)}
              tableId={tableId}
              tableType={tableType}
              label="URL Variable Mapping"
              formFields={formFields}
            />
          </div>
          <div>
            <VariableMapping
              variableData={httpRequestData?.query_variable_mapping}
              onChange={(updatedData) => handleChange("query_variable_mapping", updatedData)}
              tableId={tableId}
              tableType={tableType}
              label="Query Variable Mapping"
              formFields={formFields}
            />
          </div>
          <div>
            <VariableMapping
              variableData={httpRequestData?.header_variable_mapping}
              onChange={(updatedData) => handleChange("header_variable_mapping", updatedData)}
              tableId={tableId}
              tableType={tableType}
              label="Header Variable Mapping"
              formFields={formFields}
            />
          </div>
          <div>
            <VariableMapping
              variableData={httpRequestData?.body_variable_mapping}
              onChange={(updatedData) => handleChange("body_variable_mapping", updatedData)}
              tableId={tableId}
              tableType={tableType}
              label="Body Variable Mapping"
              formFields={formFields}
            />
          </div>
          <div>
            <VariableMapping
              variableData={httpRequestData?.output_variable_mapping}
              onChange={(updatedData) => handleChange("output_variable_mapping", updatedData)}
              tableId={tableId}
              tableType={tableType}
              label="Output Variable Mapping"
              formFields={formFields}
            />
          </div>
          <div>
            <CustomJS
              height="100px"
              inputString={httpRequestData?.output_transformation_custom_js || ""}
              handleChange={(code) => handleChange("output_transformation_custom_js", code)}
              tableID={tableId}
              table_type={tableType}
              label="Output Transformation CustomJS"
              formFields={formFields}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

export default HttpRequest;
